import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  PILLARS,
  PILLAR_COLORS,
  num,
  pct,
  pillarsApi,
  signed,
  signedPct,
  type Backtest,
  type IcWeights,
  type Weights,
} from "../../lib/pillars";
import { ACCENT, AXIS, Card, DIM, DOWN, GRID, TOOLTIP, UP } from "./chartkit";

const MARKET = "#ffca28";

/**
 * A weighting, evaluated over the whole panel.
 *
 * Running one is a deliberate click, not something a slider triggers: each distinct
 * weighting is logged as a configuration tried, and the bar a result has to clear
 * rises with the count. The verdict strip is first on the page for the same reason
 * the buy list ships with its evidence — a return chart is persuasive whether or not
 * the result means anything.
 */
export function BacktestView({
  weights,
  icWeights,
  onError,
}: {
  weights: Weights | null;
  icWeights: IcWeights | null;
  onError: (e: unknown) => void;
}) {
  const [bt, setBt] = useState<Backtest | null>(null);
  const [busy, setBusy] = useState(false);

  const run = (w: Weights | null) => {
    setBusy(true);
    pillarsApi
      .backtest(w)
      .then(setBt)
      .catch(onError)
      .finally(() => setBusy(false));
  };

  const growth = useMemo(() => {
    if (!bt) return [];
    let model = 1;
    let universe = 1;
    let market = 1;
    return bt.monthly.map((m) => {
      model *= 1 + m.model;
      universe *= 1 + m.universe;
      market *= 1 + (m.market ?? 0);
      return { period: m.period.slice(0, 7), model, universe, market };
    });
  }, [bt]);

  const button =
    "px-3 py-1 rounded border border-term-edge text-[11px] hover:border-term-accent disabled:opacity-40";

  return (
    <div className="p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button className={button} disabled={busy || !weights} onClick={() => weights && run(weights)}>
          Backtest these weights
        </button>
        <button className={button} disabled={busy} onClick={() => run(null)}>
          Backtest IC-weighted (walk-forward)
        </button>
        <span className="text-[10px] text-term-dim">
          {busy
            ? "running…"
            : "Top quintile, equal-weighted, monthly, net of costs, against the same universe held equal-weight."}
        </span>
      </div>

      {!bt && (
        <p className="text-[11px] leading-relaxed text-term-dim max-w-3xl">
          Nothing has been evaluated in this session. Each distinct weighting you backtest is
          recorded, and the luck benchmark below rises with every one — which is the price of being
          able to try them in a second.
        </p>
      )}

      {bt && (
        <>
          <Verdict bt={bt} />

          <div className="grid gap-2 grid-cols-2 md:grid-cols-4 xl:grid-cols-8">
            <Tile label="Model CAGR" value={signedPct(bt.stats.cagr_model)} />
            <Tile label="Universe (EW)" value={signedPct(bt.stats.cagr_universe)} />
            <Tile label="Market (cap-wtd)" value={signedPct(bt.stats.cagr_market)} />
            <Tile label="Max drawdown" value={pct(bt.stats.max_drawdown, 0)} />
            <Tile label="Excess / month" value={signedPct(bt.stats.excess_monthly, 2)} />
            <Tile label="Excess t" value={signed(bt.stats.excess_t)} />
            <Tile label="IC t" value={signed(bt.stats.ic_t)} />
            <Tile label="Turnover / month" value={pct(bt.stats.turnover, 0)} />
          </div>

          <div className="grid gap-3 grid-cols-1 xl:grid-cols-2">
            <Card
              title="Growth of 1"
              subtitle={`${bt.window.start} → ${bt.window.end}, log scale. Model is net of ${bt.cost_bps}bps per unit of turnover.`}
            >
              <ResponsiveContainer>
                <LineChart data={growth} margin={{ top: 8, right: 12, bottom: 0, left: -14 }}>
                  <CartesianGrid stroke={GRID} />
                  <XAxis dataKey="period" tick={AXIS} minTickGap={40} />
                  <YAxis scale="log" domain={["auto", "auto"]} tick={AXIS} tickFormatter={(v: number) => v.toFixed(1)} />
                  <Tooltip {...TOOLTIP} formatter={(v: number) => v.toFixed(2)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line dataKey="model" name="Model" stroke={UP} dot={false} isAnimationActive={false} />
                  <Line dataKey="universe" name="Universe EW" stroke={DIM} dot={false} isAnimationActive={false} />
                  <Line dataKey="market" name="Market CW" stroke={MARKET} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card
              title="Quintile returns"
              subtitle="Mean next-month return by score quintile, 5 = best scores. A ranking that works rises left to right; a lump in the middle is a bucket doing well, not a signal."
            >
              <ResponsiveContainer>
                <BarChart data={bt.quantiles} margin={{ top: 8, right: 12, bottom: 0, left: -6 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="q" tick={AXIS} />
                  <YAxis tick={AXIS} tickFormatter={(v: number) => pct(v, 1)} />
                  <Tooltip {...TOOLTIP} cursor={{ fill: GRID }} formatter={(v: number) => pct(v, 2)} />
                  <Bar dataKey="mean_fwd_return" name="mean monthly return" fill={ACCENT} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card
              title="Quarterly returns"
              subtitle="Model against the equal-weight universe and the cap-weighted market. Drag the strip to move through time."
              height={320}
            >
              <ResponsiveContainer>
                <BarChart data={bt.quarterly} margin={{ top: 8, right: 12, bottom: 0, left: -6 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="quarter" tick={AXIS} />
                  <YAxis tick={AXIS} tickFormatter={(v: number) => pct(v, 0)} />
                  <Tooltip {...TOOLTIP} cursor={{ fill: GRID }} formatter={(v: number) => signedPct(v)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <ReferenceLine y={0} stroke={DIM} />
                  <Bar dataKey="universe" name="Universe EW" fill={DIM} isAnimationActive={false} />
                  <Bar dataKey="market" name="Market CW" fill={MARKET} isAnimationActive={false} />
                  <Bar dataKey="model" name="Model" fill={UP} isAnimationActive={false} />
                  <Brush dataKey="quarter" height={18} stroke={DIM} fill="#0b0d10" startIndex={Math.max(0, bt.quarterly.length - 16)} travellerWidth={6} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card
              title="Quarterly alpha decomposition"
              subtitle="Excess over the universe, split into the held names that beat it and the ones that lagged. The line is the net, before costs."
              height={320}
            >
              <ResponsiveContainer>
                <ComposedChart data={bt.alpha} stackOffset="sign" margin={{ top: 8, right: 12, bottom: 0, left: -6 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="quarter" tick={AXIS} />
                  <YAxis tick={AXIS} tickFormatter={(v: number) => `${(v * 100).toFixed(0)}pp`} />
                  <Tooltip {...TOOLTIP} cursor={{ fill: GRID }} formatter={(v: number) => `${signed(v * 100, 1)}pp`} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <ReferenceLine y={0} stroke={DIM} />
                  <Bar dataKey="beat" name="Names that beat" stackId="a" fill={UP} isAnimationActive={false} />
                  <Bar dataKey="lagged" name="Names that lagged" stackId="a" fill={DOWN} isAnimationActive={false} />
                  <Line dataKey="net" name="Net alpha" stroke={MARKET} dot={false} isAnimationActive={false} />
                  <Brush dataKey="quarter" height={18} stroke={DIM} fill="#0b0d10" startIndex={Math.max(0, bt.alpha.length - 16)} travellerWidth={6} />
                </ComposedChart>
              </ResponsiveContainer>
            </Card>

            <Card
              title="IC of the blend, month by month"
              subtitle="Rank correlation of the score with the next month's return. Mean and t-stat are above; this shows how lumpy it is."
            >
              <ResponsiveContainer>
                <BarChart data={bt.ic_series} margin={{ top: 8, right: 12, bottom: 0, left: -14 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="date" tick={AXIS} minTickGap={40} tickFormatter={(d: string) => d.slice(0, 7)} />
                  <YAxis tick={AXIS} />
                  <Tooltip {...TOOLTIP} cursor={{ fill: GRID }} formatter={(v: number) => signed(v, 3)} />
                  <ReferenceLine y={0} stroke={DIM} />
                  <Bar dataKey="ic" name="IC" isAnimationActive={false}>
                    {bt.ic_series.map((d) => (
                      <Cell key={d.date} fill={d.ic >= 0 ? UP : DOWN} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card
              title="IC by pillar"
              subtitle="Each pillar on its own, over the same window. These do not depend on the weights — they are what the weights are choosing between."
              height="auto"
            >
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wide text-term-dim">
                  <tr className="border-b border-term-edge">
                    <th className="text-left px-2 py-1">Pillar</th>
                    <th className="text-right px-2">Mean IC</th>
                    <th className="text-right px-2">t</th>
                    <th className="text-right px-2">Hit rate</th>
                    <th className="text-right px-2">Months</th>
                  </tr>
                </thead>
                <tbody>
                  {bt.pillar_ic.map((r) => (
                    <tr key={r.pillar} className="border-b border-term-edge/50">
                      <td className="px-2 py-1" style={{ color: PILLAR_COLORS[r.pillar] }}>
                        {r.pillar}
                      </td>
                      <td className="text-right px-2 tnum">{signed(r.mean_ic, 4)}</td>
                      <td className="text-right px-2 tnum">{signed(r.t)}</td>
                      <td className="text-right px-2 tnum">{pct(r.hit, 0)}</td>
                      <td className="text-right px-2 tnum">{r.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          {bt.adaptive && icWeights && (
            <Card
              title="IC weights through time"
              subtitle={`Each month's weights come from the previous ${icWeights.lookback} months of realised pillar ICs — nothing from that month or later. Equal until ${icWeights.min_history} months of history exist.`}
              height={260}
            >
              <ResponsiveContainer>
                <AreaChart data={icWeights.series} margin={{ top: 8, right: 12, bottom: 0, left: -14 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="date" tick={AXIS} minTickGap={40} tickFormatter={(d: string) => d.slice(0, 7)} />
                  <YAxis tick={AXIS} domain={[0, 1]} tickFormatter={(v: number) => pct(v, 0)} />
                  <Tooltip {...TOOLTIP} formatter={(v: number) => pct(v, 0)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {PILLARS.map((p) => (
                    <Area key={p} dataKey={p} stackId="w" stroke={PILLAR_COLORS[p]} fill={PILLAR_COLORS[p]} fillOpacity={0.6} isAnimationActive={false} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Attribution bt={bt} />
        </>
      )}
    </div>
  );
}

function Verdict({ bt }: { bt: Backtest }) {
  const { luck, stats } = bt;
  return (
    <div className={`border rounded px-3 py-2 text-[11px] leading-relaxed ${luck.beats ? "border-term-up" : "border-term-edge"}`}>
      <span className="text-term-text">
        {bt.adaptive ? "IC-weighted (walk-forward)" : describe(bt.weights)}
      </span>
      <span className="text-term-dim"> · excess t </span>
      <span className="tnum">{signed(stats.excess_t)}</span>
      <span className="text-term-dim"> against a luck benchmark of </span>
      <span className="tnum">{num(luck.benchmark_t)}</span>
      <span className="text-term-dim"> for {luck.configurations_tried} configurations tried — </span>
      <span className={luck.beats ? "text-term-up" : "text-term-down"}>
        {luck.beats ? "clears it" : "does NOT clear it"}
      </span>
      <span className="text-term-dim">
        . This timeline has been looked at {luck.configurations_tried} times; read the charts
        below as a description of the past under these weights, not as a test of them.
      </span>
    </div>
  );
}

function describe(w: Weights | null): string {
  if (!w) return "blend";
  return PILLARS.filter((p) => w[p] > 0)
    .map((p) => `${p} ${Math.round(w[p] * 100)}%`)
    .join(" · ");
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-term-edge rounded bg-term-panel px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-term-dim">{label}</div>
      <div className="tnum text-[14px]">{value}</div>
    </div>
  );
}

/** Per-name, per-quarter excess return while held. Rows are the names held LONGEST,
 *  not the best performers — picking rows by outcome would turn a description of the
 *  book into an advertisement for it. Blank means not held, never zero. */
function Attribution({ bt }: { bt: Backtest }) {
  const { quarters, names, cells } = bt.attribution;
  const recent = quarters.slice(-24);
  const lookup = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cells) m.set(`${c.name}|${c.quarter}`, c.excess);
    return m;
  }, [cells]);

  return (
    <Card
      title="Quarterly stock attribution"
      subtitle={`Excess return against the universe in each quarter a name was held — the ${names.length} names held longest, last ${recent.length} quarters. Blank = not held.`}
      height="auto"
    >
      <div className="overflow-x-auto">
        <table className="text-[10px] tnum border-separate" style={{ borderSpacing: 1 }}>
          <thead>
            <tr className="text-term-dim">
              <th className="sticky left-0 bg-term-panel text-left px-1 w-14" />
              {recent.map((q) => (
                <th key={q} className="px-1 font-normal whitespace-nowrap">
                  {q.slice(2)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {names.map((n) => (
              <tr key={n}>
                <td className="sticky left-0 bg-term-panel text-term-accent px-1">{n}</td>
                {recent.map((q) => {
                  const v = lookup.get(`${n}|${q}`);
                  return (
                    <td key={q} className="text-center px-1 py-0.5 min-w-[44px]" style={{ background: heat(v) }}>
                      {v == null ? "" : signedPct(v, 0)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function heat(v: number | undefined): string {
  if (v == null) return "transparent";
  const a = Math.min(0.85, 0.12 + Math.abs(v) * 3);
  return v >= 0 ? `rgba(38,166,154,${a})` : `rgba(239,83,80,${a})`;
}
